import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BackdropProgressBar from "../../../../components/ui/BackdropProgressBar";
import useApi from "../../../../hooks/useApi";
import { APPROVAL_ROLES, getPifApproval } from "../../../../services/pifService";
import { useUser } from "../../../../context/UserContext";
import { PifApprovalOutcome, PifApprovalRecord } from "../types";
import PifDoneScreen from "../done/PifDoneScreen";
import PifPageBand from "../PifPageBand";
import PifApprovalReview from "./PifApprovalReview";
import { buildApprovalDoneContent } from "./approvalDoneContent";
// Carries the shared `.postif-shell` classes used by ApprovalFrame below — the same shell the
// form screens render in, so both routes overlap the band and clamp PifDoneScreen identically.
import "../PostInterviewForm.scss";

type PageStatus = "loading" | "ready" | "invalid";

/**
 * Both entries render inside the portal shell behind the shared PostIF band, like the list and the
 * form: page title in the band, breadcrumbs under it, content riding up over its lower edge. The
 * emailed /sign/:token flow used to be a standalone viewport-tall page with no chrome; it now keeps
 * the sidenav, top bar and band so the approver can navigate out of it (Epic 79820, Pooja Pujara).
 *
 * The one difference: that approver may hold no PostIF menu permission, so the trail drops its
 * "Post Interview Lists" hop, which would land them on the not-authorized page.
 */
const ApprovalFrame: FC<{ emailedLink: boolean; current?: string; children: React.ReactNode }> = ({
  emailedLink,
  current,
  children,
}) => (
  <Box className="postif-shell">
    <PifPageBand title="Post Interview Form" current={current} showListCrumb={!emailedLink} />
    <Box className="postif-shell__body">{children}</Box>
  </Box>
);

const PostIFApproval: FC = () => {
  // Two entry points, both inside the portal shell: the emailed deep link (/sign/:token) and HR
  // opening the same record by id from the list. Only the breadcrumb trail differs.
  const { id, token } = useParams<{ id?: string; token?: string }>();
  const lookupKey = token ?? id ?? "";
  const emailedLink = Boolean(token);
  const navigate = useNavigate();
  const location = useLocation();
  const { executeApi } = useApi();
  const { userId, firstName, lastName } = useUser();
  const [status, setStatus] = useState<PageStatus>("loading");
  const [record, setRecord] = useState<PifApprovalRecord | null>(null);
  const [outcome, setOutcome] = useState<{ kind: PifApprovalOutcome; signedAt: string } | null>(null);

  /**
   * The emailed token may be resolved ONCE: sProc_Portal_PostIFApprovalTokens_Resolve_U marks it
   * redeemed the first time the pending signer views it, so a second resolve of the same token
   * answers -3 (410) and reads as an invalid link. This effect used to run more than once per
   * visit - `userId` is in its dependencies and hydrates from null, and StrictMode double-invokes
   * effects in dev - and the two in-flight reads raced: whichever landed last won, so arriving
   * from login flashed "This link is no longer valid." before the form appeared.
   *
   * The ref survives StrictMode's simulated remount (state and refs are kept, only effects re-run),
   * so exactly one read is ever in flight per token and there is no race to arbitrate.
   *
   * Deliberately NOT paired with an `ignore` flag set from the effect's cleanup: StrictMode fires
   * that cleanup between the two invocations, so the first (only) read's response would be thrown
   * away while the ref blocked any replacement - leaving status stuck on "loading" and the page
   * blank. The key comparison below is the safe version of the same idea: it drops a response only
   * when a DIFFERENT token has since been requested, which cleanup timing cannot trigger.
   */
  const resolvedFor = useRef<string | null>(null);

  useEffect(() => {
    // Wait for the session: viewerIsCurrentSigner is decided by comparing the pending signer to
    // this userId, so resolving with null would spend the token and still render read-only.
    if (!lookupKey || !userId || resolvedFor.current === lookupKey) return;
    resolvedFor.current = lookupKey;

    (async () => {
      const result = await executeApi(() => getPifApproval(lookupKey, userId), undefined);
      if (resolvedFor.current !== lookupKey) return;
      setRecord(result);
      setStatus(result ? "ready" : "invalid");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey, userId]);

  const handleOutcome = (kind: PifApprovalOutcome, _pifId: string, signedAt: string) => setOutcome({ kind, signedAt });
  // No PDF-generation backend exists yet — the browser's print dialog is the closest real behavior.
  const handleDownloadPdf = () => window.print();
  // Only offered on the in-portal /:id entry — see the emailedLink guards at the call sites.
  const handleBackToQueue = () => navigate({ pathname: "/hiring/postif", search: location.search });

  // `loading` is useApi's flag, which is false both before the read starts and after it ends -
  // passing it here rendered nothing at all in either gap, so any stall showed as a blank page
  // rather than a spinner. While this screen says it is loading, it shows a spinner.
  if (status === "loading") return <BackdropProgressBar loading />;

  if (status === "invalid" || !record) {
    return (
      // No record to name, so the list is the terminal crumb.
      <ApprovalFrame emailedLink={emailedLink}>
        <PifDoneScreen
          icon={<InfoOutlinedIcon />}
          tone="warning"
          title="This link is no longer valid."
          lede="It may have expired, already been used, or isn't addressed to your account. Ask the sender for a fresh link if you still need to review this form."
          // Emailed path: no portal menu, so a queue button would land on a no-access page
          // (PR 30856). In-portal the crumb is also a way back, but the button stays — it is the
          // affordance main added for this screen.
          actions={
            emailedLink
              ? []
              : [{ label: "Back to my queue", variant: "contained" as const, onClick: handleBackToQueue, testId: "postif-approval-backtoqueue" }]
          }
        />
      </ApprovalFrame>
    );
  }

  const signerName = `${firstName ?? ""} ${lastName ?? ""}`.trim() || APPROVAL_ROLES[record.currentRole].label;

  return (
    // The band lives here rather than inside PifApprovalReview so that component stays router-free
    // (its test suite can't resolve react-router-dom under CRA's Jest).
    <ApprovalFrame emailedLink={emailedLink} current={record.pifId}>
      {outcome ? (
        <PifDoneScreen
          {...buildApprovalDoneContent(record.pifId, record.currentRole, outcome.kind, outcome.signedAt)}
          // Same dead end as the invalid-link screen above, one decision later: an emailed approver
          // who has just signed has no queue to go back to either. The PDF stands on its own.
          actions={[
            { label: "Print signed PDF", variant: "outlined", onClick: handleDownloadPdf, testId: "postif-approval-downloadpdf" },
            ...(emailedLink
              ? []
              : [{ label: "Back to my queue", variant: "contained" as const, onClick: handleBackToQueue, testId: "postif-approval-backtoqueue" }]),
          ]}
        />
      ) : (
        <PifApprovalReview
          record={record}
          signerName={signerName}
          onBack={emailedLink ? undefined : handleBackToQueue}
          onOutcome={handleOutcome}
        />
      )}
    </ApprovalFrame>
  );
};

export default PostIFApproval;
